import React from "react";
import * as Icons from "lucide-react";
import { Link } from "react-router-dom";

// Pick the first catalog-like module (products / services / rooms etc.) to power the app preview
const CATALOG_KEYS = [
  "services","products","menu","collections","packages","rooms","properties","departments","academics","features",
];

const ACCENT_LABEL_BY_INDUSTRY = {
  "Healthcare": { greet: "Hi", primaryAction: "Book Appointment", bottom: ["Home", "Doctors", "Search", "Profile"] },
  "Dentistry": { greet: "Hi", primaryAction: "Book a Slot", bottom: ["Home", "Doctors", "Care", "Profile"] },
  "Wellness": { greet: "Welcome", primaryAction: "Book Therapy", bottom: ["Home", "Therapies", "Cart", "Me"] },
  "Beauty": { greet: "Hello", primaryAction: "Shop Now", bottom: ["Shop", "Search", "Cart", "Me"] },
  "Fitness": { greet: "Let's go", primaryAction: "Start Workout", bottom: ["Home", "Plans", "Track", "Me"] },
  "K-12 Education": { greet: "Welcome", primaryAction: "Apply Now", bottom: ["Home", "Academics", "Notice", "Me"] },
  "Higher Education": { greet: "Welcome", primaryAction: "Apply Now", bottom: ["Home", "Courses", "Library", "Me"] },
  "Hospitality": { greet: "Namaste", primaryAction: "Book Stay", bottom: ["Stays", "Search", "Trips", "Me"] },
  "Travel": { greet: "Namaste", primaryAction: "Book Package", bottom: ["Explore", "Packages", "Trips", "Me"] },
  "Food Service": { greet: "Hungry?", primaryAction: "Order Now", bottom: ["Menu", "Cart", "Orders", "Me"] },
  "F&B": { greet: "Hungry?", primaryAction: "Order Now", bottom: ["Menu", "Cart", "Orders", "Me"] },
  "Gourmet Food": { greet: "Welcome", primaryAction: "Shop Now", bottom: ["Shop", "Search", "Cart", "Me"] },
  "Retail Grocery": { greet: "Hi", primaryAction: "Order Now", bottom: ["Shop", "Cart", "Orders", "Me"] },
  "Apparel": { greet: "Hello", primaryAction: "Shop New", bottom: ["Shop", "Looks", "Cart", "Me"] },
  "Home & Living": { greet: "Welcome", primaryAction: "Shop Now", bottom: ["Shop", "Rooms", "Cart", "Me"] },
  "Real Estate": { greet: "Hi", primaryAction: "Schedule Visit", bottom: ["Listings", "Saved", "Search", "Me"] },
  "Legal": { greet: "Welcome", primaryAction: "Book Consultation", bottom: ["Home", "Cases", "Calendar", "Me"] },
  "Automotive": { greet: "Welcome", primaryAction: "Book Slot", bottom: ["Services", "Garage", "Bookings", "Me"] },
  "SaaS / Marketing": { greet: "Welcome back", primaryAction: "Run Campaign", bottom: ["Home", "Campaigns", "Inbox", "Me"] },
};

const BOTTOM_ICONS = {
  Home: "Home", Doctors: "Stethoscope", Search: "Search", Profile: "User", Me: "User",
  Therapies: "Flower", Cart: "ShoppingBag", Shop: "Store", Plans: "ListChecks", Track: "Activity",
  Academics: "BookOpen", Notice: "Bell", Courses: "GraduationCap", Library: "BookOpen",
  Stays: "BedDouble", Trips: "Map", Explore: "Compass", Packages: "Plane",
  Menu: "UtensilsCrossed", Orders: "ClipboardList", Looks: "Sparkles",
  Rooms: "LayoutGrid", Listings: "Building", Saved: "Heart",
  Cases: "Briefcase", Calendar: "Calendar", Services: "Wrench", Garage: "Car", Bookings: "CalendarCheck",
  Campaigns: "Send", Inbox: "Inbox", Care: "HeartPulse",
};

const fallbackInfo = { greet: "Welcome", primaryAction: "Get Started", bottom: ["Home", "Search", "Cart", "Me"] };

export default function MobileAppPreview({ template }) {
  if (!template) return null;
  const info = ACCENT_LABEL_BY_INDUSTRY[template.industry] || fallbackInfo;

  // Pick a primary catalog module
  const catalogKey = CATALOG_KEYS.find((k) => template.modules[k]?.items?.length);
  const catalog = catalogKey ? template.modules[catalogKey] : null;
  const tiles = (catalog?.items || []).slice(0, 4);

  // Hero card content (first tile)
  const heroTile = tiles[0];

  // Time string for status bar
  const now = "9:41";

  return (
    <section
      className="relative py-16 sm:py-24 overflow-hidden"
      data-testid="section-mobile-app-preview"
      style={{
        background: `linear-gradient(135deg, color-mix(in oklab, var(--tpl-primary) 8%, var(--tpl-bg)) 0%, var(--tpl-bg) 100%)`,
      }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        {/* LEFT — copy & feature bullets */}
        <div>
          <p className="text-xs uppercase tracking-[0.24em] font-semibold mb-3" style={{ color: "var(--tpl-primary)" }}>
            Your Business App Preview
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            This is how your{" "}
            <span style={{ color: "var(--tpl-primary)" }}>{template.brand.name.split(" ")[0]}</span>{" "}
            mobile app experience can look.
          </h2>
          <p className="mt-4 text-base opacity-75 max-w-lg">
            Customers stay one tap away from your business — browse, book and pay
            from a beautifully branded app on iOS &amp; Android. Same design
            language. Same brand. More loyalty.
          </p>

          <ul className="mt-7 space-y-3">
            {[
              { icon: "Smartphone", label: "iOS + Android in one branded app" },
              { icon: "BellRing", label: "Push notifications for offers &amp; updates" },
              { icon: "CreditCard", label: "Built-in UPI / cards / wallets checkout" },
              { icon: "BarChart3", label: "Owner dashboard with realtime analytics" },
            ].map((f, i) => {
              const Cmp = Icons[f.icon] || Icons.Sparkles;
              return (
                <li key={i} className="flex items-start gap-3" data-testid={`mobile-app-feature-${i}`}>
                  <span
                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: `linear-gradient(135deg, color-mix(in oklab, var(--tpl-primary) 18%, transparent), color-mix(in oklab, var(--tpl-accent) 12%, transparent))`,
                      color: "var(--tpl-primary)",
                    }}
                  >
                    <Cmp className="w-4 h-4" />
                  </span>
                  <span className="text-sm leading-relaxed opacity-90" dangerouslySetInnerHTML={{ __html: f.label }} />
                </li>
              );
            })}
          </ul>

          <Link
            to={`/templates/${template.key}/${template.nav.find((n) => n.type === "contact")?.slug || "contact"}`}
            className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-white"
            style={{ background: `linear-gradient(135deg, var(--tpl-primary), var(--tpl-accent))` }}
            data-testid="mobile-app-cta"
          >
            Get this for my business <Icons.ArrowRight className="w-4 h-4" />
          </Link>
          <p className="mt-3 text-xs opacity-60">
            UI preview only — this is how a real app for your business could look.
          </p>
        </div>

        {/* RIGHT — phone mockup */}
        <div className="relative flex justify-center" data-testid="mobile-app-mockup">
          {/* Soft glow */}
          <div
            className="absolute inset-0 blur-3xl opacity-50 -z-0"
            style={{ background: `radial-gradient(circle at 50% 40%, color-mix(in oklab, var(--tpl-primary) 35%, transparent), transparent 65%)` }}
          />

          <div
            className="relative w-[290px] sm:w-[320px] aspect-[9/19] rounded-[3rem] p-[10px] shadow-2xl"
            style={{
              background: "#0c0c10",
              boxShadow: `0 30px 80px color-mix(in oklab, var(--tpl-primary) 40%, transparent), 0 0 0 1px rgba(255,255,255,0.06)`,
            }}
          >
            {/* Notch */}
            <div
              className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 rounded-b-2xl z-20"
              style={{ background: "#0c0c10" }}
            />

            {/* Screen */}
            <div
              className="relative w-full h-full rounded-[2.5rem] overflow-hidden flex flex-col"
              style={{ background: "var(--tpl-surface)", color: "var(--tpl-text)" }}
            >
              {/* Status bar */}
              <div className="flex items-center justify-between px-6 pt-3 pb-2 text-[10px] font-semibold">
                <span>{now}</span>
                <div className="flex items-center gap-1 opacity-80">
                  <Icons.Signal className="w-3 h-3" />
                  <Icons.Wifi className="w-3 h-3" />
                  <Icons.BatteryFull className="w-3.5 h-3.5" />
                </div>
              </div>

              {/* App header */}
              <div className="px-5 pt-2 pb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-white text-sm"
                    style={{ background: `linear-gradient(135deg, var(--tpl-primary), var(--tpl-accent))` }}
                  >
                    {template.brand.name.charAt(0)}
                  </div>
                  <div className="leading-tight">
                    <p className="text-[10px] opacity-60">{info.greet} 👋</p>
                    <p className="font-display font-bold text-sm">{template.brand.name.split(" ").slice(0, 2).join(" ")}</p>
                  </div>
                </div>
                <button
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "color-mix(in oklab, var(--tpl-text) 6%, transparent)" }}
                  aria-label="Notifications"
                >
                  <Icons.Bell className="w-4 h-4" style={{ color: "var(--tpl-primary)" }} />
                </button>
              </div>

              {/* Scrollable content */}
              <div className="flex-1 overflow-hidden px-4 space-y-3">
                {/* Hero card */}
                {heroTile && (
                  <div
                    className="relative rounded-2xl overflow-hidden h-28"
                    style={{
                      background: heroTile.image
                        ? `url(${heroTile.image}) center/cover`
                        : `linear-gradient(135deg, var(--tpl-primary), var(--tpl-accent))`,
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="relative h-full flex flex-col justify-end p-3 text-white">
                      <p className="text-[10px] uppercase tracking-widest opacity-80">Featured</p>
                      <p className="text-sm font-bold leading-tight">{heroTile.title}</p>
                      <button
                        className="mt-2 self-start text-[10px] font-semibold px-2.5 py-1 rounded-full"
                        style={{ background: "rgba(255,255,255,0.95)", color: "var(--tpl-primary)" }}
                      >
                        {info.primaryAction}
                      </button>
                    </div>
                  </div>
                )}

                {/* Feature grid */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[11px] font-bold">For you</p>
                    <p className="text-[10px] opacity-60">See all</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {tiles.slice(0, 4).map((t, i) => (
                      <div
                        key={i}
                        className="rounded-xl p-2 border"
                        style={{
                          backgroundColor: "color-mix(in oklab, var(--tpl-text) 3%, var(--tpl-surface))",
                          borderColor: "color-mix(in oklab, var(--tpl-text) 8%, transparent)",
                        }}
                      >
                        {t.image ? (
                          <div className="aspect-square rounded-lg overflow-hidden mb-1.5">
                            <img src={t.image} alt={t.title} className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div
                            className="aspect-square rounded-lg mb-1.5 flex items-center justify-center"
                            style={{
                              background: `linear-gradient(135deg, color-mix(in oklab, var(--tpl-primary) 18%, transparent), color-mix(in oklab, var(--tpl-accent) 14%, transparent))`,
                              color: "var(--tpl-primary)",
                            }}
                          >
                            {(() => {
                              const Cmp = Icons[t.icon] || Icons.Sparkles;
                              return <Cmp className="w-5 h-5" />;
                            })()}
                          </div>
                        )}
                        <p className="text-[10px] font-semibold leading-tight line-clamp-2">{t.title}</p>
                        {t.price && (
                          <p className="text-[9px] mt-0.5" style={{ color: "var(--tpl-primary)" }}>
                            {t.price}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bottom nav */}
              <div
                className="grid grid-cols-4 px-2 pt-2 pb-3 border-t"
                style={{ borderColor: "color-mix(in oklab, var(--tpl-text) 8%, transparent)" }}
              >
                {info.bottom.map((label, i) => {
                  const IconName = BOTTOM_ICONS[label] || "Circle";
                  const Cmp = Icons[IconName] || Icons.Circle;
                  const active = i === 0;
                  return (
                    <div key={label} className="flex flex-col items-center gap-0.5">
                      <Cmp
                        className="w-4 h-4"
                        style={{ color: active ? "var(--tpl-primary)" : "color-mix(in oklab, var(--tpl-text) 50%, transparent)" }}
                      />
                      <span
                        className="text-[9px] font-medium"
                        style={{ color: active ? "var(--tpl-primary)" : "color-mix(in oklab, var(--tpl-text) 55%, transparent)" }}
                      >
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Floating mini-badges */}
          <div
            className="hidden sm:flex absolute -left-4 top-16 items-center gap-2 px-3 py-2 rounded-2xl shadow-lg"
            style={{ background: "var(--tpl-surface)", border: "1px solid color-mix(in oklab, var(--tpl-text) 10%, transparent)" }}
          >
            <Icons.ShieldCheck className="w-4 h-4" style={{ color: "var(--tpl-primary)" }} />
            <span className="text-[11px] font-semibold">Secure checkout</span>
          </div>
          <div
            className="hidden sm:flex absolute -right-2 bottom-24 items-center gap-2 px-3 py-2 rounded-2xl shadow-lg"
            style={{ background: "var(--tpl-surface)", border: "1px solid color-mix(in oklab, var(--tpl-text) 10%, transparent)" }}
          >
            <Icons.Star className="w-4 h-4 fill-current" style={{ color: "var(--tpl-accent)" }} />
            <span className="text-[11px] font-semibold">4.9 rating</span>
          </div>
        </div>
      </div>
    </section>
  );
}
